/**
 * Helper functions to calculate and attach manpower data to tasks
 */

import { Task, ManpowerData } from './types';
import {
  calculateManpowerRequirements,
  calculateManpowerCosts,
  estimateTaskDuration,
  getTotalLaborCost,
  getWorkTypesForCategory
} from './manpowerUtils';
import {
  getManpowerWorkType,
  getAllLaborRates,
  getAllWorkforce
} from './manpowerConfig';

/**
 * Calculate manpower data for a single task
 * 
 * @param task - The task to calculate manpower for
 * @param allConstants - Array of all manpower constants
 * @param laborRates - Optional custom labor rates (uses config if not provided)
 * @param workforce - Optional custom workforce (uses config if not provided)
 * @returns ManpowerData or null if calculation fails
 */
export const calculateTaskManpower = (
  task: Task,
  allConstants: any[], // Should be ManpowerConstant[]
  laborRates?: Record<string, number>,
  workforce?: Record<string, number>
): ManpowerData | null => {
  try {
    // Get configuration
    const rates = laborRates || getAllLaborRates();
    const workers = workforce || getAllWorkforce();

    // Get available work types for the category
    const availableWorkTypes = getWorkTypesForCategory(allConstants, task.category);
    
    // Debug logging
    if (availableWorkTypes.length === 0) {
      console.log(`[Manpower] No work types for category: "${task.category}"`);
      // List available categories
      const categories = [...new Set(allConstants.map((c: any) => c.category))];
      console.log(`[Manpower] Available categories:`, categories.slice(0, 10));
    }

    // Map task subcategory to manpower work type
    const workType = getManpowerWorkType(
      task.category,
      task.subCategory,
      availableWorkTypes
    );

    // Calculate requirements
    const requirements = calculateManpowerRequirements(
      allConstants,
      task.category,
      workType,
      task.quantity,
      task.taskUnit
    );

    // If no requirements found, return null
    if (requirements.length === 0) {
      console.log(`[Manpower] No requirements for: "${task.category}" - "${workType}" (subCat: "${task.subCategory}")`);
      return null;
    }

    // Calculate costs
    const costs = calculateManpowerCosts(
      allConstants,
      task.category,
      workType,
      task.quantity,
      rates,
      task.taskUnit
    );

    // Estimate duration
    const duration = estimateTaskDuration(
      allConstants,
      task.category,
      workType,
      task.quantity,
      workers,
      task.taskUnit
    );

    // Calculate total cost
    const totalLaborCost = getTotalLaborCost(costs);

    return {
      requirements,
      costs,
      estimatedDuration: duration.estimatedDuration,
      totalLaborCost,
      workType
    };
  } catch (error) {
    console.error('Error calculating manpower:', error);
    return null;
  }
};

/**
 * Add manpower data to a task
 * Modifies the task object in place and returns it
 */
export const enhanceTaskWithManpower = (
  task: Task,
  allConstants: any[],
  laborRates?: Record<string, number>,
  workforce?: Record<string, number>
): Task => {
  const manpower = calculateTaskManpower(task, allConstants, laborRates, workforce);

  if (manpower) {
    task.manpower = manpower;
  }

  return task;
};

/**
 * Add manpower data to multiple tasks
 */
export const enhanceTasksWithManpower = (
  tasks: Task[],
  allConstants: any[],
  laborRates?: Record<string, number>,
  workforce?: Record<string, number>
): Task[] => {
  return tasks.map(task => enhanceTaskWithManpower(task, allConstants, laborRates, workforce));
};

/**
 * Recalculate manpower for a task when quantity changes
 */
export const recalculateManpower = (
  task: Task,
  newQuantity: number,
  allConstants: any[],
  laborRates?: Record<string, number>,
  workforce?: Record<string, number>
): ManpowerData | null => {
  const updatedTask = {
    ...task,
    quantity: newQuantity
  };

  return calculateTaskManpower(updatedTask, allConstants, laborRates, workforce);
};

/**
 * Get total manpower requirements across multiple tasks
 */
export const aggregateManpowerRequirements = (
  tasks: Task[]
): Record<string, number> => {
  const aggregated: Record<string, number> = {};

  tasks.forEach(task => {
    if (task.manpower) {
      task.manpower.requirements.forEach(req => {
        if (!aggregated[req.labour]) {
          aggregated[req.labour] = 0;
        }
        aggregated[req.labour] += req.daysRequired;
      });
    }
  });

  return aggregated;
};

/**
 * Get total manpower costs across multiple tasks
 */
export const aggregateManpowerCosts = (
  tasks: Task[]
): { total: number; byLabor: Record<string, number> } => {
  let total = 0;
  const byLabor: Record<string, number> = {};

  tasks.forEach(task => {
    if (task.manpower) {
      total += task.manpower.totalLaborCost;

      task.manpower.costs.forEach(cost => {
        if (!byLabor[cost.labour]) {
          byLabor[cost.labour] = 0;
        }
        byLabor[cost.labour] += cost.totalCost;
      });
    }
  });

  return { total, byLabor };
};

/**
 * Calculate workforce utilization
 * Returns percentage utilized for each labor type
 */
export const calculateWorkforceUtilization = (
  tasks: Task[],
  timeframeDays: number = 20, // Default to 1 month (20 working days)
  workforce?: Record<string, number>
): Record<string, { needed: number; available: number; utilization: number; overloaded: boolean }> => {
  const workers = workforce || getAllWorkforce();
  const requirements = aggregateManpowerRequirements(tasks);
  const utilization: Record<string, any> = {};

  Object.keys(workers).forEach(labour => {
    const needed = requirements[labour] || 0;
    const available = workers[labour] * timeframeDays; // worker-days available
    const utilizationPct = available > 0 ? (needed / available) * 100 : 0;

    utilization[labour] = {
      needed,
      available,
      utilization: utilizationPct,
      overloaded: utilizationPct > 100
    };
  });

  return utilization;
};

/**
 * Suggest optimal workforce allocation
 * Returns recommended worker count for each labor type
 */
export const suggestWorkforceAllocation = (
  tasks: Task[],
  timeframeDays: number = 20
): Record<string, { current: number; recommended: number; reason: string }> => {
  const currentWorkforce = getAllWorkforce();
  const requirements = aggregateManpowerRequirements(tasks);
  const suggestions: Record<string, any> = {};

  Object.keys(requirements).forEach(labour => {
    const needed = requirements[labour];
    const current = currentWorkforce[labour] || 0;
    const recommended = Math.ceil(needed / timeframeDays);

    let reason = '';
    if (recommended > current) {
      const shortage = recommended - current;
      reason = `Add ${shortage} more worker${shortage > 1 ? 's' : ''} to meet demand`;
    } else if (recommended < current) {
      const excess = current - recommended;
      reason = `Can reduce by ${excess} worker${excess > 1 ? 's' : ''} (overallocated)`;
    } else {
      reason = 'Optimal allocation';
    }

    suggestions[labour] = {
      current,
      recommended,
      reason
    };
  });

  return suggestions;
};

/**
 * Check if tasks can be completed with available workforce
 */
export const canCompleteWithWorkforce = (
  tasks: Task[],
  timeframeDays: number = 20,
  workforce?: Record<string, number>
): { possible: boolean; bottlenecks: string[] } => {
  const utilization = calculateWorkforceUtilization(tasks, timeframeDays, workforce);
  const bottlenecks: string[] = [];

  Object.entries(utilization).forEach(([labour, data]) => {
    if (data.overloaded) {
      bottlenecks.push(`${labour}: ${data.utilization.toFixed(0)}% utilized (${data.needed.toFixed(1)} days needed, ${data.available.toFixed(1)} available)`);
    }
  });

  return {
    possible: bottlenecks.length === 0,
    bottlenecks
  };
};

/**
 * Compare estimated vs actual manpower usage
 * (Requires actual labor tracking data)
 */
export const compareEstimatedVsActual = (
  task: Task,
  actualLaborDays: Record<string, number>
): Record<string, { estimated: number; actual: number; variance: number; efficiency: number }> => {
  if (!task.manpower) {
    return {};
  }

  const comparison: Record<string, any> = {};

  task.manpower.requirements.forEach(req => {
    const actual = actualLaborDays[req.labour] || 0;
    const estimated = req.daysRequired;
    const variance = actual - estimated;
    const efficiency = estimated > 0 ? (estimated / actual) * 100 : 0;

    comparison[req.labour] = {
      estimated,
      actual,
      variance,
      efficiency
    };
  });

  return comparison;
};

